// Адмін: реферальна аналітика — хто скільки привів. Розгортаєш рядок — бачиш
// кожного друга (дата, тестів, відповідей) і позначку «підозріло» для накрутки.
import { useState } from 'react';
import { api } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

export function AdminReferrals() {
  const [range, setRange] = useState<'contest' | 'all'>('all');
  const [open, setOpen] = useState<string | null>(null);
  const list = useFetch(() => api.admin.referrals(range), [range]);

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 flex-1 text-xs text-tg-hint">
          {range === 'contest'
            ? 'Вікно конкурсу 14–24.09 (скасовано). «Активний» = пройшов ≥1 тест.'
            : 'За весь час. «Активний» = пройшов ≥1 тест.'}
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => setRange((r) => (r === 'contest' ? 'all' : 'contest'))}
            className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs"
          >
            {range === 'contest' ? 'Весь час' : '🏆 Конкурс'}
          </button>
          <button onClick={list.reload} className="rounded-lg bg-tg-secondary-bg px-3 py-1.5 text-xs">
            ↻
          </button>
        </div>
      </div>

      {list.loading && <Loader />}
      {list.error && <ErrorView error={list.error} onRetry={list.reload} />}

      {list.data && (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat label="Рефереров" value={list.data.totals.referrers} />
            <Stat label="Запрошено" value={list.data.totals.invited} />
            <Stat label="Активних" value={list.data.totals.active} accent="success" />
          </div>

          {list.data.leaderboard.length === 0 && (
            <p className="card text-sm text-tg-hint">Поки нікого не привели.</p>
          )}

          {list.data.leaderboard.map((r, i) => {
            const isOpen = open === r.id;
            const label = r.username ? `@${r.username}` : r.name ?? `id ${r.id}`;
            return (
              <article key={r.id} className="card space-y-2">
                <button
                  onClick={() => setOpen(isOpen ? null : r.id)}
                  className="flex w-full items-center gap-3 text-left"
                >
                  <div
                    className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-bold ${
                      i === 0
                        ? 'bg-yellow-500/20 text-yellow-500'
                        : i < 3
                          ? 'bg-tg-button/20 text-tg-button'
                          : 'bg-tg-bg text-tg-hint'
                    }`}
                  >
                    {i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{label}</div>
                    <div className="text-[11px] text-tg-hint">
                      id {r.id} · запрошено {r.invited}
                      {r.suspicious > 0 && (
                        <span className="text-yellow-500"> · ⚠️ підозрілих {r.suspicious}</span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-lg font-bold text-tg-success">{r.active}</div>
                    <div className="text-[10px] text-tg-hint">активних</div>
                  </div>
                  <span className="text-tg-hint">{isOpen ? '▾' : '▸'}</span>
                </button>

                {isOpen && (
                  <div className="space-y-1 border-t border-tg-secondary-bg pt-2">
                    {r.friends.map((f) => (
                      <div
                        key={f.id}
                        className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs ${
                          f.suspicious ? 'bg-yellow-500/10' : 'bg-tg-bg'
                        }`}
                      >
                        <span className="min-w-0 flex-1 truncate">
                          {f.username ? `@${f.username}` : f.name ?? `id ${f.id}`}
                        </span>
                        <span className="shrink-0 text-tg-hint">
                          {new Date(f.joinedAt).toLocaleDateString('uk-UA', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className={`shrink-0 ${f.active ? 'text-tg-success' : 'text-tg-hint'}`}>
                          {f.tests} тест. · {f.answers} відп.
                        </span>
                        {f.suspicious && <span className="shrink-0">⚠️</span>}
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: 'success' }) {
  return (
    <div className="card py-2">
      <div className={`text-lg font-bold ${accent === 'success' ? 'text-tg-success' : ''}`}>{value}</div>
      <div className="text-[11px] text-tg-hint">{label}</div>
    </div>
  );
}
